import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTelegram } from 'react-icons/fa';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { pageTransition, staggerContainer, staggerItem, tapScale, tapScaleTransition } from '../helpers/animations';
import { api, normalizeToken } from '../helpers';
import { API_BASE_URL } from '../helpers/config';
import { SupportSkeleton } from '../components/skeletons';

const TERMS = [
  'By using TradeExchanger, you agree to comply with all applicable laws and regulations.',
  'You are responsible for the accuracy of information provided during exchange requests.',
  'TradeExchanger reserves the right to suspend or terminate accounts that violate these terms.',
  'Rates and fees are subject to change. Check the app for current pricing before transacting.',
  'Support is available via Telegram only. Response times may vary.',
  'We do not store your password. Keep your credentials secure.',
  'Disputes should be raised through our Telegram support channel.',
];

function Support() {
  const { token: tokenParam } = useParams();
  const token = normalizeToken(tokenParam);
  const [termsOpen, setTermsOpen] = useState(false);
  const [support, setSupport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSupport = async () => {
      setLoading(true);
      try {
        const { data } = await api.post(`${API_BASE_URL}/exchange/support`, token ? { token } : {});
        if (data?.error === false && data?.data) {
          setSupport(data.data);
        }
      } catch {
        // ignore, fallback to config-based handle
      } finally {
        setLoading(false);
      }
    };

    fetchSupport();
  }, [token]);

  const handleName = support?.username || '';
  const TELEGRAM_URL = handleName ? `https://t.me/${handleName}` : '';

  const handleOpenTelegram = () => {
    if (!TELEGRAM_URL) return;
    window.open(TELEGRAM_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <motion.div
      {...pageTransition}
      transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="support-page"
    >
      <motion.h1
        className="screen-title"
        variants={staggerItem}
        initial="initial"
        animate="animate"
      >
        Support
      </motion.h1>

      {loading ? (
        <SupportSkeleton />
      ) : (
        <>
      <motion.div
        className="support-telegram-card"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05, duration: 0.25 }}
      >
        <div className="support-telegram-icon">
          <FaTelegram size={40} />
        </div>
        <h2 className="support-telegram-title">Contact us on Telegram</h2>
        <p className="support-telegram-desc">
          For any issue, question, or exchange help — we reply on Telegram.
        </p>
        <motion.button
          type="button"
          className="support-telegram-btn"
          onClick={handleOpenTelegram}
          whileTap={tapScale}
          transition={tapScaleTransition}
          disabled={!handleName}
        >
          <FaTelegram size={20} />
          <span>{handleName ? `@${handleName}` : 'Unavailable'}</span>
        </motion.button>
      </motion.div>

      <motion.div
        className="support-quick-card"
        variants={staggerContainer}
        initial="initial"
        animate="animate"
      >
        <p className="support-quick-text">Tap the button above to start a chat. We typically respond within 24 hours.</p>
      </motion.div>

      <motion.div
        className="support-terms-card"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.12, duration: 0.25 }}
      >
        <motion.button
          type="button"
          className="support-terms-toggle"
          onClick={() => setTermsOpen(!termsOpen)}
          whileTap={tapScale}
          transition={tapScaleTransition}
        >
          <span>Terms & Conditions</span>
          {termsOpen ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
        </motion.button>
        <AnimatePresence>
          {termsOpen && (
            <motion.ul
              className="support-terms-list"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {TERMS.map((term, i) => (
                <li key={i}>{term}</li>
              ))}
            </motion.ul>
          )}
        </AnimatePresence>
      </motion.div>
        </>
      )}
    </motion.div>
  );
}

export default Support;
